import React from 'react';
import {
  useLoaderData,
  useRouter,
} from '@tanstack/react-router';
import { usePromiseModal } from '@prezly/react-promise-modal';
import { toast } from 'sonner';

import Pagination from '../../Pagination';
import PaginationHeader from '../../PaginationHeader';
import SearchField from '../../SearchField';
import DashboardAdminAssetGroup from './AssetGroup';
import AssetGroupDeleteModal from './AssetGroupDeleteModal';
import AssetGroupEditModal from './AssetGroupEditModal';
import Loading from '../../Loading';

import { useStore } from '../../../store';

import type { AssetGroup } from '../../../types/asset-group';

const DashboardAdminAssetGroups: React.FC = () => {
  const searching = useStore.getState().searching;
  const { assetGroups: assetGroupsData } = useLoaderData({ strict: false });
  const assetGroups = assetGroupsData?.data || [];
  const totalAssetGroups = assetGroupsData?.meta.total;
  const perPage = assetGroupsData?.meta.per_page;

  const router = useRouter();

  const editAssetGroup = usePromiseModal<boolean, { assetGroup?: AssetGroup }>(
    ({ show, onSubmit, onDismiss, assetGroup }) => (
      <AssetGroupEditModal
        assetGroup={assetGroup}
        show={show}
        onConfirm={() => onSubmit(true)}
        onDismiss={onDismiss}
      />
    )
  );

  const handleEditAssetGroup = async (assetGroup: AssetGroup) => {
    const response = await editAssetGroup.invoke({ assetGroup });
    if (response === true) {
      toast.success('Group updated successfully');
      await router.invalidate();
    }
  };

  const handleCreateAssetGroup = async () => {
    const response = await editAssetGroup.invoke({});
    if (response === true) {
      toast.success('Group created successfully');
      await router.invalidate();
    }
  };

  const deleteAssetGroup = usePromiseModal<boolean, { assetGroup: AssetGroup }>(
    ({ show, onSubmit, onDismiss, assetGroup }) => (
      <AssetGroupDeleteModal
        assetGroup={assetGroup}
        show={show}
        onConfirm={(response: boolean) => onSubmit(response)}
        onDismiss={onDismiss}
      />
    )
  );

  const handleDeleteAssetGroup = async (assetGroup: AssetGroup) => {
    const response = await deleteAssetGroup.invoke({ assetGroup }) as boolean;
    if (response === true) {
      toast.success('Group deleted successfully');
      router.navigate({
        to: '.',
        search: (prev: any) => ({
          ...prev,
          t: new Date().getTime(),
        }),
      });
      router.invalidate({
        filter: (route) => {
          return route.routeId === window.location.pathname;
        },
      });
      setTimeout(() => {
        router.navigate({
          to: '.',
          search: (prev: any) => ({
            ...prev,
            t: undefined,
          }),
        });
      }, 100);
    } else if (response === false) {
      toast.error('Failed to delete group');
    }
  };

  return (
    <>
      <div className="dashboard-header page-header wa-cluster">
        <div>
          <h2 className="page-title">Image <em>groups</em></h2>
          <p>All groups of SPRK images</p>
        </div>
        <div>
          <wa-button size="s" onClick={handleCreateAssetGroup}>
            New image group
          </wa-button>
        </div>
      </div>
      <div className="dashboard-admin dashboard-admin-users wa-stack wa-gap-0 inner">
        <div className="wa-stack">
          <div className="total-items">
            <PaginationHeader text="Group" total={totalAssetGroups} perPage={perPage} />
          </div>
          <SearchField placeholder="Search groups..." />
        </div>
        {searching ? (
          <Loading internal />
        ) : (
          <div className="dashboard-admin-users-list wa-stack">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Visible from</th>
                  <th>Visible to</th>
                  <th>Images</th>
                  <th className="actions">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {assetGroups.map((assetGroup: AssetGroup) => (
                  <DashboardAdminAssetGroup
                    key={assetGroup.id}
                    assetGroup={assetGroup}
                    handleEditAssetGroup={handleEditAssetGroup}
                    handleDeleteAssetGroup={handleDeleteAssetGroup}
                  />
                ))}
              </tbody>
            </table>
            <Pagination total={totalAssetGroups} perPage={perPage} />
          </div>
        )}
      </div>
      {deleteAssetGroup.modal}
      {editAssetGroup.modal}
    </>
  );
}

export default DashboardAdminAssetGroups;
